import React, { ReactNode, useEffect, useRef } from "react";
import { LDProvider, useLDClient } from "launchdarkly-react-client-sdk";
import type { LDLogger } from "launchdarkly-react-client-sdk";
import { datadogLogs } from "@datadog/browser-logs";
import { isEmpty } from "./utils/Util";

interface LaunchDarklyProviderProps {
  children: ReactNode;
  dealerId: number;
  userEmail: string | null;
}

// Launch Darkly Client Side Id
const clientSideID = process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_SIDE_ID;

// The LD SDK's own warn/error text is written for SDK maintainers, not for someone
// scanning a log list (e.g. "The waitForInitialization function was called without
// a timeout specified..."). Recognized messages get anexplanation for
// the log's main Content; anything unrecognized falls back to the raw SDK text so we
// never silently swallow a message we haven't seen before. The raw text is always
// kept in the sdkMessage attribute either way, for anyone who needs the exact wording.
const explainSdkMessage = (message: string): string => {
  if (
    message.includes("waitForInitialization") &&
    message.includes("timeout")
  ) {
    return (
      "connected without a wait-time limit on the initial connection — this is expected, " +
      "the app waits as long as needed for real flag data instead of using a default cutoff"
    );
  }
  if (message.includes("stream connection")) {
    return "lost its live connection and is retrying";
  }
  if (
    message.includes("Error fetching flag settings") ||
    message.includes("network error")
  ) {
    return "could not reach LaunchDarkly's servers to get feature flag values";
  }
  return message;
};

// LaunchDarklyProvider is used for exactly one of a dealer session (dealerId set) or
// a TL-domain user session (userEmail set) at a time — never both, never neither.
// Resolving this once gives every log call the same label/context and guarantees
// it's logged exactly once, with a safe fallback if that invariant is ever broken.
const getLdIdentity = (dealerId?: number, userEmail?: string | null) => {
  if (!isEmpty(dealerId)) {
    return { label: `dealerId ${dealerId}`, context: { dealerId } };
  }
  if (!isEmpty(userEmail)) {
    return { label: `userEmail ${userEmail}`, context: { userEmail } };
  }
  return { label: "unknown dealer/user", context: {} };
};

// LD's default logger writes warn/error output straight to the browser console.
// Route it through Datadog instead so LD failures don't surface as raw console noise.
const createLdLogger = (dealerId?: number, userEmail?: string | null): LDLogger => {
  const { label, context } = getLdIdentity(dealerId, userEmail);
  return {
    debug: () => {},
    info: () => {},
    // NOTE: the context key must not be named "message" — Datadog merges the context
    // object into the log entry, and a "message" key there overwrites the log's own
    // title (e.g. "LaunchDarkly warning") with this value instead of adding to it.
    //
    // The dealer/user identity and a explanation are folded into the
    // log's main message (Content column in Datadog) so it's readable at a glance
    // in the log list, without opening the entry to inspect attributes.
    warn: (message) => {
      datadogLogs.logger.warn(`LaunchDarkly warning for ${label}: ${explainSdkMessage(message)}`, {
        ...context,
        sdkMessage: message,
      });
    },
    error: (message) => {
      datadogLogs.logger.error(`LaunchDarkly error for ${label}: ${explainSdkMessage(message)}`, {
        ...context,
        sdkMessage: message,
      });
    },
  };
};

// Description per LD SDK error type, so the log's main message reads
// like a sentence instead of a bare class name. Falls back to a generic description
// for error types not explicitly listed here.
const FRIENDLY_ERROR_DESCRIPTIONS: Record<string, string> = {
  LDFlagFetchError: "could not fetch the latest feature flags",
  LDUnexpectedResponseError: "received an unexpected error response",
  LDInvalidDataError: "received invalid data",
  LDInvalidArgumentError: "was called with an invalid argument",
};

const BASE_BACKOFF_MS = 2_000;
const MAX_BACKOFF_MS = 60_000;

// Genuine LD client errors (unreachable event/flag endpoints, stream failures) are emitted
// via the ldClient 'error' event, which fires once per failed flush/poll — during an outage
// that can be every couple of seconds. Report them to Datadog with enough context to diagnose,
// backing off exponentially per error type so a prolonged outage doesn't flood telemetry.
const LDErrorReporter: React.FC<{ dealerId?: number; userEmail?: string | null }> = ({
  dealerId,
  userEmail,
}) => {
  const ldClient = useLDClient();
  const nextAllowedAt = useRef<Map<string, number>>(new Map());
  const failureCount = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    const { label, context } = getLdIdentity(dealerId, userEmail);

    const handleError = (error: Error) => {
      const errorType = error?.name || "LDError";
      const now = Date.now();
      if (now < (nextAllowedAt.current.get(errorType) ?? 0)) {
        return;
      }

      const description =
        FRIENDLY_ERROR_DESCRIPTIONS[errorType] || "reported a client error";
      datadogLogs.logger.error(`LaunchDarkly ${description} for ${label}`, {
        ...context,
        errorType,
        sdkMessage: error?.message,
        timestamp: new Date(now).toISOString(),
      });

      const count = failureCount.current.get(errorType) ?? 0;
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** count, MAX_BACKOFF_MS);
      nextAllowedAt.current.set(errorType, now + delay);
      failureCount.current.set(errorType, count + 1);
    };

    ldClient.on("error", handleError);
    return () => ldClient.off("error", handleError);
  }, [ldClient, dealerId, userEmail]);

  return null;
};

const LaunchDarklyProvider: React.FC<LaunchDarklyProviderProps> = ({
  children,
  dealerId,
  userEmail,
}) => {
  const user = userEmail
    ? {
        key: `user-${userEmail}`, // unique per dealer
        custom: {
          email: userEmail,
        },
      }
    : {
        key: `Dealer-${dealerId}`, // unique per dealer
        custom: {
          dealerId: Number(dealerId),
        },
      };

  return (
    <LDProvider
      clientSideID={clientSideID!}
      context={user}
      options={{
        // No localStorage bootstrap: with bootstrap enabled the SDK serves a cached
        // (possibly stale) flag value before the real fetch lands, so a dealer could
        // keep acting on a value LaunchDarkly had already changed — defeating a
        // kill-switch. Flag consumers treat a missing flag as "off" safely, so no
        // fallback value is needed here.
        logger: createLdLogger(dealerId, userEmail),
        // The SDK retries failed analytics-event POSTs on a fixed interval (2s minimum,
        // enforced by the SDK) with no backoff of its own. Raising it means a sustained
        // LD outage retries every 15s instead of every 2s, without delaying flag delivery
        // (flags are pushed via streaming, not tied to this interval).
        flushInterval: 15_000,
      }}
    >
      <LDErrorReporter dealerId={dealerId} userEmail={userEmail} />
      {children}
    </LDProvider>
  );
};

export default LaunchDarklyProvider;
